import { UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PlanTypeEnum } from '@ortho-next/nextray-core';
import Slider from 'bootstrap-slider';
import { first } from 'rxjs/operators';
import { AnatomicalSideEnum, APP_CONFIG, BoneTypeEnum, convertLength, convertMass, convertToISOString, convertToLocalDate, dateFormat, DeviceEnum, ExpressionOperator, GenderEnum, LanguageService, SearchFields } from '../../core';
import { ISearchControl, SearchControl, searchErrorMsgs, userNameExists } from '../../models';
import { ListService, UserService } from '../../services';
import { AnatomicalSideLabelPipe, BaseComponent, BoneTypeLabelPipe, DateRange, DaterangepickerComponent, DeviceLabelPipe, FormValidationMsgDirective, PlanTypeLabelPipe } from '../../shared';


/**
* Filters component to filter patient and plan list.
*/
@Component({
	selector: 'filters',
	templateUrl: './filters.component.html',
	imports: [
		FormsModule,
		UpperCasePipe,
		DeviceLabelPipe,
		BoneTypeLabelPipe,
		PlanTypeLabelPipe,
		ReactiveFormsModule,
		AnatomicalSideLabelPipe,
		DaterangepickerComponent,
		FormValidationMsgDirective
	],
	changeDetection: ChangeDetectionStrategy.Eager
})
export class FiltersComponent extends BaseComponent implements OnInit, OnDestroy {

	private readonly config = inject(APP_CONFIG);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);
	private readonly formBuilder = inject(FormBuilder);
	protected override readonly langSrv = inject(LanguageService);

	readonly searchForm = this.formBuilder.nonNullable.group<ISearchControl>(new SearchControl());
	readonly errorMsgs = searchErrorMsgs;

	isCustomerCare = false;
	isFilterVisible = new Array<boolean>(10).fill(false);
	isAllFiltersCollapsed = false;
	planDateRange: DateRange | null = null;
	uptodateFilter = false;
	outdatedFilter = false;
	preOpFilter = false;
	postOpFilter = false;
	ageSlider: any;
	weightSlider: any;
	heightSlider: any;

	readonly dateFormat = dateFormat();
	readonly deviceList = [DeviceEnum.Tlhex, DeviceEnum.Fitbone, DeviceEnum.Jps];
	readonly genderList: GenderEnum[] = Object.values(GenderEnum);
	readonly sideList: AnatomicalSideEnum[] = Object.values(AnatomicalSideEnum);
	readonly boneList: BoneTypeEnum[] = Object.values(BoneTypeEnum);
	readonly planTypeList: PlanTypeEnum[] = Object.values(PlanTypeEnum);

	filtersForm: FormGroup = this.formBuilder.group({
		device: this.initEnumGroup(this.deviceList),
		gender: this.initEnumGroup(this.genderList),
		side: this.initEnumGroup(this.sideList),
		bone: this.initEnumGroup(this.boneList),
		planType: this.initEnumGroup(this.planTypeList)
	});;

	private readonly convertWeight = (val: number) => val === 0 ? 0 : convertMass(val, 'kg');
	private readonly convertHeight = (val: number) => val === 0 ? 0 : convertLength(val, 'cm');

	ngOnInit(): void {
		// console.log(typeof (globalThis as any).Zone, inject(NgZone).constructor.name);
		this.initSliders();
		this.isAllFiltersCollapsed = this._isAllFiltersCollapsed;
		this.userSrv.isCustomerCare().pipe(first()).subscribe(res => this.isCustomerCare = res);
	}

	private initSliders(): void {
		const ageOptions = { id: "slider12c", min: 0, max: 100, step: 1, range: true, value: [0, 100], tooltip: 'hide' };
		this.ageSlider = new Slider("#ageSlider", ageOptions).on('slideStop', (val: any) => this.submitAge(val));

		const measureOptions = { id: "slider12c", min: 0, max: 250, step: 0.1, range: true, value: [0, 250], tooltip: 'hide' };
		this.weightSlider = new Slider("#weightSlider", measureOptions).on('slideStop', (val: any) => this.submitWeight(val));
		this.heightSlider = new Slider("#heightSlider", measureOptions).on('slideStop', (val: any) => this.submitHeight(val));
	}

	/**
	* Get Plan Creation Date range in string format.
	*/
	get planDateFormat(): string {
		return this.planDateRange ? `${convertToLocalDate(this.planDateRange.from, false)} - ${convertToLocalDate(this.planDateRange.to, false)}` : '';
	};

	/**
	* Get Plan Device form group.
	*/
	get deviceGroup(): FormArray {
		return this.filtersForm.get('device') as FormArray;
	};

	/**
	* Get Patient Gender form group.
	*/
	get genderGroup(): FormArray {
		return this.filtersForm.get('gender') as FormArray;
	};

	/**
	* Get Plan Side form group.
	*/
	get sideGroup(): FormArray {
		return this.filtersForm.get('side') as FormArray;
	};

	/**
	* Get plan bone type form group.
	*/
	get boneGroup(): FormArray {
		return this.filtersForm.get('bone') as FormArray;
	};

	/**
	* Get plan type form group.
	*/
	get planTypeGroup(): FormArray {
		return this.filtersForm.get('planType') as FormArray;
	};

	/**
	* Get Patient Age range in string format.
	*/
	get rangeAge(): string {
		return `${this.ageSlider?.getValue()[0]} -  ${this.ageSlider.getValue()[1]} ${this.labels().FILTERS_COMPONENT_YEARS}`;
	}

	/**
	* Get Patient Weight range in kg in string format.
	*/
	get rangeWeightKg(): string {
		return `${this.weightSlider.getValue()[0]} -  ${this.weightSlider.getValue()[1]} Kg`;
	}

	/**
	* Get Patient Weight range in lb in string format.
	*/
	get rangeWeightLb(): string {
		return `${this.convertWeight(this.weightSlider.getValue()[0])} -  ${this.convertWeight(this.weightSlider.getValue()[1])} Lb`;
	}

	/**
	* Get Patient height range in cm in string format.
	*/
	get rangeHeightCm(): string {
		return `${this.heightSlider.getValue()[0]} - ${this.heightSlider.getValue()[1]} Cm`;
	}

	/**
	* Get Patient Height range in ft in string format.
	*/
	get rangeHeightFt(): string {
		return `${this.convertHeight(this.heightSlider.getValue()[0])} - ${this.convertHeight(this.heightSlider.getValue()[1])} Ft`;
	}

	/**
	* Update Plan Creation Date string format.
	*/
	updatePlanDateRange(range: DateRange | null): void {
		this.planDateRange = range;
	}

	changeUptodateFilter(): void {
		this.uptodateFilter = !this.uptodateFilter;
		this.submitPlanVersion();
	}
	changeOutdatedFilter(): void {
		this.outdatedFilter = !this.outdatedFilter;
		this.submitPlanVersion();
	}

	/**
	* Submit plan version filter and update list.
	*/
	private submitPlanVersion(): void {
		if (this.outdatedFilter && !this.uptodateFilter) {
			this.listSrv.setFilter(SearchFields.planVersion, this.config.appVersion, ExpressionOperator.Ne);
			this.listSrv.setFilter(SearchFields.planId, 1, ExpressionOperator.Ge); // prevent only patients
		}
		else if (!this.outdatedFilter && this.uptodateFilter) {
			this.listSrv.setFilter(SearchFields.planVersion, this.config.appVersion, ExpressionOperator.Eq);
			this.listSrv.setFilter(SearchFields.planId, 0, ExpressionOperator.Ge); // remove filter
		} else if (this.outdatedFilter && this.uptodateFilter) {
			this.listSrv.setFilter(SearchFields.planVersion, '.', ExpressionOperator.Contains);
			this.listSrv.setFilter(SearchFields.planId, 0, ExpressionOperator.Ge); // remove filter
		} else {
			this.listSrv.setFilter(SearchFields.planVersion, 0, ExpressionOperator.Contains); // remove filter
			this.listSrv.setFilter(SearchFields.planId, 0, ExpressionOperator.Ge); // remove filter
		}
		this.listSrv.search();
	}

	private resetPlanVersion(): void {
		(document.getElementById("outdatedFilter") as HTMLInputElement).checked = false;
		this.outdatedFilter = false;
		(document.getElementById("uptodateFilter") as HTMLInputElement).checked = false;
		this.uptodateFilter = false;
	}

	/**
	* Submit plan device filter and update list.
	*/
	submitDevice(result: boolean[]) {
		const selected = this.getSelectedEnums(this.deviceList, result);
		this.listSrv.setFilter(SearchFields.device, selected, ExpressionOperator.In);
		this.listSrv.search();
	}

	/**
	* Submit patient gender filter and update list.
	*/
	submitGender(result: boolean[]) {
		const selected = this.getSelectedEnums(this.genderList, result);
		this.listSrv.setFilter(SearchFields.patGender, selected, ExpressionOperator.In);
		this.listSrv.search();
	}

	/**
	* Submit plan side filter and update list.
	*/
	submitSide(result: boolean[]) {
		const selected = this.getSelectedEnums(this.sideList, result);
		this.listSrv.setFilter(SearchFields.side, selected, ExpressionOperator.In);
		this.listSrv.search();
	}

	/**
	* Submit plan bone type filter and update list.
	*/
	submitBone(result: boolean[]) {
		const selected = this.getSelectedEnums(this.boneList, result);
		this.listSrv.setFilter(SearchFields.boneType, selected, ExpressionOperator.In);
		this.listSrv.search();
	}

	/**
	* Submit plan type filter and update list.
	*/
	submitPlanType(result: boolean[]) {
		const selected = this.getSelectedEnums(this.planTypeList, result);
		this.listSrv.setFilter(SearchFields.planType, selected, ExpressionOperator.In);
		this.listSrv.search();
	}

	changePreOpFilter(): void {
		this.preOpFilter = !this.preOpFilter;
		this.submitPlanScenario();
	}

	changePostOpFilter(): void {
		this.postOpFilter = !this.postOpFilter;
		this.submitPlanScenario();
	}

	/**
	* Submit plan step filter and update list.
	*/
	private submitPlanScenario(): void {
		if (this.preOpFilter && !this.postOpFilter) {
			this.listSrv.setFilter(SearchFields.isPostOperative, true, ExpressionOperator.Ne);
		}
		else if (!this.preOpFilter && this.postOpFilter) {
			this.listSrv.setFilter(SearchFields.isPostOperative, true, ExpressionOperator.Eq);
		}
		else {
			this.listSrv.setFilter(SearchFields.isPostOperative, true, ExpressionOperator.In);
		}
		this.listSrv.search();
	}

	private resetPlanScenario(): void {
		(document.getElementById("preOpFilter") as HTMLInputElement).checked = false;
		this.preOpFilter = false;
		(document.getElementById("postOpFilter") as HTMLInputElement).checked = false;
		this.postOpFilter = false;
	}

	/**
	* Submit patient age filter and update list.
	*/
	submitAge(result: number[]): void {
		const from = result[0] === 0 ? null : result[0];
		const to = result[1] === 100 ? null : result[1];
		this.listSrv.setRangeFilter(SearchFields.patAge, from, ExpressionOperator.Ge, to, ExpressionOperator.Le);
		this.listSrv.search();
	}

	/**
	* Submit patient weight filter and update list.
	*/
	submitWeight(result: number[]): void {
		const from = result[0] === 0 ? null : result[0];
		const to = result[1] === 250 ? null : result[1];
		this.listSrv.setRangeFilter(SearchFields.patWeight, from, ExpressionOperator.Ge, to, ExpressionOperator.Le);
		this.listSrv.search();
	}

	/**
	* Submit patient height filter and update list.
	*/
	submitHeight(result: number[]): void {
		const from = result[0] === 0 ? null : result[0];
		const to = result[1] === 250 ? null : result[1];
		this.listSrv.setRangeFilter(SearchFields.patHeight, from, ExpressionOperator.Ge, to, ExpressionOperator.Le);
		this.listSrv.search();
	}


	private initEnumGroup(list: any[]): FormArray {
		const arr = list.map(s => {
			return this.formBuilder.control(false);
		})
		return this.formBuilder.array(arr);
	}

	private getSelectedEnums<T>(list: T[], value: boolean[]): T[] | null {
		const selected = value.map((s, i) => {
			return s ? list[i] : null;
		}).filter(s => !!s);
		return selected.length ? selected as T[] : null;
	}

	/**
	* Update list according customer care role.
	*/
	search(): void {
		if (this.isCustomerCare) {
			this.applyCustomerCareFilters();
		} else {
			this.applyMainFilters();
		}
	}

	private applyCustomerCareFilters(): void {
		this.listSrv.setDeletedFilter(!!this.searchForm.value.showDeleted);

		if (this.searchForm.value.userName) {
			this.userSrv.getUserGuid(this.searchForm.value.userName).subscribe(guid => {
				if (guid) {
					this.listSrv.setFilter(SearchFields.userGuid, guid, ExpressionOperator.Eq);
					this.applyMainFilters();
				} else {
					this.searchForm.controls.userName.setErrors({ [`${userNameExists}`]: null });
				}
			});
		} else {
			this.listSrv.setFilter(SearchFields.userGuid, null, ExpressionOperator.Eq);
			this.applyMainFilters();
		}
	}

	private applyMainFilters(): void {
		this.listSrv.setFilter(SearchFields.patNumber, this.searchForm.value.patientId, ExpressionOperator.Contains);
		this.listSrv.setFilter(SearchFields.planNumber, this.searchForm.value.planId, ExpressionOperator.Contains);

		if (this.planDateRange) {
			const fromDate = convertToISOString(this.planDateRange.from);
			const toDate = convertToISOString(this.planDateRange.to);
			this.listSrv.setRangeFilter(SearchFields.planModifiedDate, fromDate, ExpressionOperator.Ge, toDate, ExpressionOperator.Le);
		} else {
			this.listSrv.setFilter(SearchFields.planModifiedDate, null, ExpressionOperator.Le);
		}

		this.listSrv.search();
	}

	/**
	* Reset all filters.
	*/
	clearFilters(): void {
		this.searchForm.reset();
		this.planDateRange = null;
		this.filtersForm.reset();
		this.resetPlanVersion();
		this.resetPlanScenario();
		this.ageSlider.setValue([0, 100]);
		this.weightSlider.setValue([0, 250]);
		this.heightSlider.setValue([0, 250]);
		this.listSrv.resetFilters();
		this.search();
	}

	/**
	* Reset Plan Creation Date filter.
	*/
	planDateClear(): void {
		this.planDateRange = null;
	}

	/**
	* Collapse all filters.
	*/
	collapseAllFilters(): void {
		this.isFilterVisible = this.isFilterVisible.map(_ => false);
		this.isAllFiltersCollapsed = this._isAllFiltersCollapsed;
	}

	/**
	* Show all filters.
	*/
	showAllFilters(): void {
		this.isFilterVisible = this.isFilterVisible.map(_ => true);
		this.isAllFiltersCollapsed = this._isAllFiltersCollapsed;
	}

	/**
	* Collpase/show only selected filter.
	*/
	changeFilterVisibility(index: number): void {
		this.isFilterVisible[index] = !this.isFilterVisible[index];
		this.isAllFiltersCollapsed = this._isAllFiltersCollapsed;
	}

	private get _isAllFiltersCollapsed(): boolean {
		return !this.isFilterVisible.includes(true);
	}


	ngOnDestroy(): void {
		this.listSrv.resetFilters();
	}

}
